import I18n from 'I18n';
import { Avatar } from 'avatar-components/Avatar';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Select } from 'foundations-components/transitional/components/Select';
import { virtualizedMenuRenderer } from 'foundations-components/transitional/utils/virtualizedMenuRenderer';
import { List as ImmutableList } from 'immutable';
import { useMemo } from 'react';
import { convertListToArray } from '../../shared/utils/immutable';
import { useTeamOptions } from '../../team/hooks/useTeamOptions';
import { useRoutableUserOptions } from '../../users/hooks/useRoutableUserOptions';
import { useConvertToTeamOptions } from '../hooks/useConvertToTeamOptions';
import { useSelectItemComponentShim } from '../hooks/useSelectItemComponentShim';
import { useSelectedUsersAndTeams } from '../hooks/useSelectedUsersAndTeams';
import { useSpecificUsers } from '../hooks/useSpecificUsers';
import { useUserAndTeamOptionIds } from '../hooks/useUserAndTeamOptionIds';
import { useUsersAndTeamsPlaceholder } from '../hooks/useUsersAndTeamsPlaceholder';
import { searchResults } from '../operators/searchResults';
import { selectUserTeamIds } from '../utils/selected-users-teams';
import AgentSelectItem from './AgentSelectItem';
import ChatFlowInboxAccessTooltip from './tooltips/ChatFlowInboxAccessTooltip';
import { jsx as _jsx } from "react/jsx-runtime";
const renderAvatar = email => {
  if (!email) {
    return null;
  }
  return /*#__PURE__*/_jsx("div", {
    style: {
      width: '16px',
      height: '16px'
    },
    children: /*#__PURE__*/_jsx(Avatar, {
      size: "xxs",
      hubSpotUserEmail: email
    })
  });
};
const getGroupedOptionsFromLists = (usersOptions, teamsOptions, inboxId, hideUserSectionTooltip) => {
  const groupOptions = [{
    text: I18n.text('routing-ui.users-and-teams.groups.team'),
    options: teamsOptions
  }, {
    text: I18n.text('routing-ui.users-and-teams.groups.user'),
    dropdownText: typeof inboxId !== 'undefined' && !hideUserSectionTooltip ? /*#__PURE__*/_jsx(ChatFlowInboxAccessTooltip, {
      inboxId: `${inboxId}`
    }) : null,
    options: usersOptions.map(user => Object.assign({}, user, {
      avatar: renderAvatar(user.email)
    }))
  }];
  return groupOptions;
};
const checkWithImmutable = props => props.withImmutable === true;
export const UsersAndTeamsSelect = props => {
  // Note: doing this so that we can allow Immutable and non-Immutable values to
  // be sent to `handleChange`; this is a temporary solution until we remove Immutable
  // support completely
  const {
    'data-test-id': testId,
    disabled,
    dropDownFooter,
    error,
    hideUserSectionTooltip,
    inboxId,
    itemComponent,
    objectTypeId,
    routingSource,
    selectedTeamIds = [],
    selectedUserIds = [],
    valueComponent = AgentSelectItem
  } = props;
  const workspaceId = Number(inboxId);
  const {
    data: teams,
    loading: loadingTeams,
    teams: teamOptions
  } = useTeamOptions({
    convertToOptions: useConvertToTeamOptions(routingSource),
    objectTypeId,
    routingSource,
    skipFetch: false,
    workspaceId
  });
  const specificUsers = useSpecificUsers(convertListToArray(selectedUserIds));
  const {
    loading: loadingUsers,
    userOptions
  } = useRoutableUserOptions({
    objectTypeId,
    routingSource,
    specificUsers,
    workspaceId
  });
  const optionIds = useUserAndTeamOptionIds({
    teamOptions,
    userOptions
  });
  const options = useMemo(() => getGroupedOptionsFromLists(userOptions, teamOptions, inboxId, hideUserSectionTooltip), [hideUserSectionTooltip, inboxId, teamOptions, userOptions]);
  const {
    teamIds,
    userIds
  } = useMemo(() => ({
    teamIds: convertListToArray(selectedTeamIds),
    userIds: convertListToArray(selectedUserIds)
  }), [selectedTeamIds, selectedUserIds]);
  const {
    normalizeTeams,
    selectedIds
  } = useSelectedUsersAndTeams({
    teamIds,
    userIds,
    teams
  });
  const placeholder = useUsersAndTeamsPlaceholder(teamOptions, userOptions);
  const ItemComponent = useSelectItemComponentShim(itemComponent);
  if (loadingTeams || loadingUsers) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {});
  }
  const onChange = event => {
    const newIds = selectUserTeamIds(event.target.value, optionIds);
    const normalizedTeamIds = normalizeTeams(newIds.teams);

    // we use `props` here to satisfy TS which won't understand the type of `handleChange`
    // if we call it directly
    // TODO: remove when Immutable support is dropped
    if (checkWithImmutable(props)) {
      props.handleChange(ImmutableList(newIds.users), ImmutableList(normalizedTeamIds));
    } else {
      props.handleChange(newIds.users, normalizedTeamIds);
    }
  };
  return /*#__PURE__*/_jsx(FormControl, {
    error: !!error,
    validationMessage: error || null,
    children: /*#__PURE__*/_jsx(Select, {
      anchorType: "button",
      cache: false,
      "data-test-id": testId || 'uses-and-teams-select',
      disabled: disabled,
      dropdownFooter: dropDownFooter,
      filterOptions: searchResults,
      itemComponent: ItemComponent,
      menuRenderer: virtualizedMenuRenderer,
      menuStyles: {
        maxBlockSize: '320px'
      },
      multi: true,
      onChange: onChange,
      options: options,
      placeholder: placeholder,
      value: selectedIds,
      valueComponent: valueComponent
    })
  });
};
UsersAndTeamsSelect.displayName = 'UsersAndTeamsSelect';
export default UsersAndTeamsSelect;